import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import { Box, Grid, List, MenuItem, Select, Stack, styled, Typography } from '@mui/material';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { BiCalendar, BiLocationPlus } from 'react-icons/bi';
import {
  ChipsType,
  Label,
  WsAutoComplete,
  WsButton,
  WsColors,
  WsDatePicker,
  WsDateTimePicker,
  WsDrawer,
  WsFilterDialog,
  WsInputNumber,
  WsLocationSearch,
  WsSelect,
  WsSelectObj,
  WsTagsList,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { WsSearchFilter } from '../../../../../components';
import { JobTitleFilter, WsAccordion } from '../../../../../components/filters/components';
import { visaOptions } from '../../../helper/VisaOptions';

const DrawerFooter = styled(Grid)(() => ({
  display: 'flex',
  alignItems: 'center',
  position: 'fixed',
  bottom: 1,
  left: 2,
  right: 2,
  justifyContent: 'flex-end',
}));

const StyledSelect = styled(Select)(() => ({
  height: 20,
  width: 155,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  fontSize: '14px',
}));

const Filters = () => {
  const [isDateTime, setDateTime] = useState<boolean>(false);
  const [filtersArray, setFiltersArray] = useState<string[]>([]);
  const [openAdvancedFilters, setOpenAdvancedFilters] = useState<boolean>(false);
  const methods = useForm();
  const availability: WsSelectObj[] = [
    {
      label: 'Not Available',
      value: 'NotAvailable',
    },
    {
      label: 'Available Immediately',
      value: 'AvailableImmediately',
    },
    {
      label: 'Available in 7 Days',
      value: 'AvailableIn7Days',
    },
    {
      label: 'Available In 15 Days',
      value: 'AvailableIn15Days',
    },
    {
      label: 'Available In 30 Days',
      value: 'AvailableIn30Days',
    },
  ];

  const onSubmit = (data) => {
    data.locationFilter?.forEach((location) => {
      const locationFilter = `${location.city},${location.countryCode}`;
      if (!filtersArray.includes(locationFilter)) filtersArray.push(locationFilter);
    });
    const queryFilter = data.query;
    const availabilityFilter = data.availability;
    if (queryFilter && !filtersArray.includes(queryFilter)) filtersArray.push(queryFilter);
    if (availabilityFilter && !filtersArray.includes(availabilityFilter))
      filtersArray.push(availabilityFilter);
    setFiltersArray([...filtersArray]);
    console.log(data);
  };
  return (
    <FormProvider {...methods}>
      <Box my={1} component="form" onSubmit={methods.handleSubmit(onSubmit)}>
        <WsSearchFilter
          name="filterConsultants"
          showDefaultRightActions={true}
          showLeftActions={true}
          showChildren={true}
          onClickFilter={() => setOpenAdvancedFilters(true)}
          leftActionsChildren={
            <Grid container mt={4} height="52px">
              <Grid item container xs={3} height="52px">
                <WsFilterDialog
                  label="Location"
                  name="All"
                  icon={<BiLocationPlus size={26} color={WsColors.TheBlues500} />}
                >
                  <Grid px={1} py={2} item xs={4} minWidth={480}>
                    <WsLocationSearch
                      name="locationFilter"
                      placeHolder="Search Location"
                      size="small"
                    />
                  </Grid>
                </WsFilterDialog>
              </Grid>
              <Grid item container xs={3} height="52px" display="flex">
                <Grid item xs={1}>
                  <Box pt={1}>
                    <LibraryBooksIcon fontSize="small" color="primary" />
                  </Box>
                </Grid>
                <Grid item xs={11}>
                  <Stack
                    direction="column"
                    sx={{ justifyContent: 'flex-start', alignItems: 'flex-start' }}
                  >
                    <Box pl={2}>
                      <Typography
                        color={WsColors.TertiaryShades600}
                        fontSize="10px"
                        fontWeight="bold"
                      >
                        Availability
                      </Typography>
                    </Box>
                    <Box pl={2}>
                      <StyledSelect
                        name="availability"
                        disableUnderline={true}
                        size="small"
                        displayEmpty
                      >
                        {availability.map((e) => (
                          <MenuItem value={e.value} key={e.value}>
                            <WsTypography variant={WsTypoVariants.BODY2}>{e.label}</WsTypography>
                          </MenuItem>
                        ))}
                      </StyledSelect>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
              <Grid item container xs={3} height="52px" display="flex">
                <Grid item xs={1}>
                  <Box pt={1}>
                    <LibraryBooksIcon fontSize="small" color="primary" />
                  </Box>
                </Grid>
                <Grid item xs={11}>
                  <Stack
                    direction="column"
                    sx={{ justifyContent: 'flex-start', alignItems: 'flex-start' }}
                  >
                    <Box pl={2}>
                      <Typography
                        color={WsColors.TertiaryShades600}
                        fontSize="10px"
                        fontWeight="bold"
                      >
                        Visa Status
                      </Typography>
                    </Box>
                    <Box pl={2}>
                      <StyledSelect
                        name="visaStatus"
                        disableUnderline={true}
                        size="small"
                        displayEmpty
                      >
                        {visaOptions.map((e) => (
                          <MenuItem value={e.value} key={e.value}>
                            <WsTypography variant={WsTypoVariants.BODY2}>{e.label}</WsTypography>
                          </MenuItem>
                        ))}
                      </StyledSelect>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
              <Grid item xs={3} height="52px">
                <WsFilterDialog
                  label="Posted On"
                  name="postedOn"
                  icon={<BiCalendar size={26} color={WsColors.TheBlues500} />}
                >
                  <Box p={1}>
                    {!isDateTime && (
                      <Stack spacing={2} direction="row" pt={3} pl={1}>
                        <WsDatePicker name="fromDate" label="From Date" disableFuture={true} />
                        <WsDatePicker
                          name="toDate"
                          label="To Date"
                          disableFuture={true}
                          minDate={Date.prototype}
                        />
                      </Stack>
                    )}
                    {isDateTime && (
                      <Stack spacing={2} direction="row" pt={3} pl={1}>
                        <WsDateTimePicker name="fromDate" label="From Date" disableFuture={true} />
                        <WsDateTimePicker
                          name="toDate"
                          label="To Date"
                          disableFuture={true}
                          minDate={Date.prototype}
                        />
                      </Stack>
                    )}
                    {!isDateTime && (
                      <Box px={1}>
                        <WsButton variant="text" size="small" onClick={() => setDateTime(true)}>
                          select Date And Time
                        </WsButton>
                      </Box>
                    )}
                    {isDateTime && (
                      <Box px={1}>
                        <WsButton variant="text" size="small" onClick={() => setDateTime(false)}>
                          select Date Only
                        </WsButton>
                      </Box>
                    )}
                  </Box>
                </WsFilterDialog>
              </Grid>
            </Grid>
          }
        />
      </Box>

      <WsDrawer
        title="Advanced Filters"
        open={openAdvancedFilters}
        onClose={() => setOpenAdvancedFilters(false)}
        size="medium"
        headerSize="medium"
      >
        <Box component="form" role="presentation" onSubmit={() => null} onReset={() => null}>
          <Box pb={7}>
            <List>
              <JobTitleFilter name="jobType" />
            </List>
            <List>
              <WsAccordion title="Willing To Relocate">
                <WsSelect
                  name="willingToRelocate"
                  selectArray={[
                    { label: 'Yes', value: 1 },
                    { label: 'No', value: 0 },
                  ]}
                  placeholder="Select"
                />
              </WsAccordion>
            </List>
            <List>
              <WsAccordion title="Experience">
                <WsInputNumber name="experience" label="" />
              </WsAccordion>
            </List>
            <List>
              <WsAccordion title="Created By">
                <WsAutoComplete
                  // onValueEnter={() => null}
                  onTagsChange={() => null}
                  placeholder="Filter By Email"
                  initialValue={[]}
                  optionsInStringArray={[]}
                  multiple={true}
                  freeSolo={false}
                  size="small"
                  name="createdBy"
                />
              </WsAccordion>
            </List>
            <List>
              <WsAccordion title="Visibility">
                <WsSelect name="internalVisibility" selectArray={[]} placeholder="Select" />
              </WsAccordion>
            </List>
            <List>
              <WsAccordion title="Visible to Wisestep Recruiter Network">
                <WsSelect name="internalVisibility" selectArray={[]} placeholder="Select" />
              </WsAccordion>
            </List>
            <List>
              <WsAccordion title="Owner">
                <WsAutoComplete
                  // onValueEnter={() => null}
                  onTagsChange={() => null}
                  placeholder="Filter By Email"
                  initialValue={[]}
                  optionsInStringArray={[]}
                  multiple={true}
                  freeSolo={false}
                  size="small"
                  name="createdBy"
                />
              </WsAccordion>
            </List>
          </Box>
          <DrawerFooter>
            <Stack p={1} spacing={2} direction="row">
              <WsButton variant="outlined" size="large" type="reset">
                Reset
              </WsButton>
              <WsButton variant="contained" size="large" type="submit">
                Apply
              </WsButton>
            </Stack>
          </DrawerFooter>
        </Box>
      </WsDrawer>

      {filtersArray.length > 0 && (
        <Box>
          <Label sx={{ fontWeight: 'bold' }}>Results generated for:</Label>
          <WsTagsList
            chipArray={filtersArray}
            count={100}
            type={ChipsType.PRIMARY}
            allowDelete={true}
            onDelete={(chip) => {
              setFiltersArray((prev) => {
                prev.splice(prev.indexOf(chip), 1);
                return [...prev];
              });
            }}
          />
        </Box>
      )}
    </FormProvider>
  );
};

export default Filters;
